import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Droplets, Wind, Gauge, Sun } from 'lucide-react';
import { WeatherData } from '../../types/weather';
import { WeatherCondition, getWeatherConditionLabel } from '../../utils/weather';
import { computeWeatherCondition } from '../../utils/weather';
import { SUN_LOCATION } from '../../utils/constants';

interface WeatherCardProps {
  weatherData: WeatherData | null;
}

const getWeatherIcon = (condition: WeatherCondition): string => {
  const iconMap: Record<WeatherCondition, string> = {
    sunny: '/Sunny.svg',
    cloudy: '/Cloudy.svg',
    'partly-cloudy': '/Partly Cloudy.svg',
    rainy: '/Rainy.svg',
    foggy: '/Foggy.svg',
    hot: '/Hot and Dry.svg',
    cold: '/Cold.svg',
    unknown: '/Sunny.svg',
  };
  return iconMap[condition] || iconMap.unknown;
};

export const WeatherCard: React.FC<WeatherCardProps> = ({ weatherData }) => {
  const [sunTimes, setSunTimes] = React.useState<{
    sunrise: string | null;
    sunset: string | null;
    loading: boolean;
  }>({
    sunrise: null,
    sunset: null,
    loading: true,
  });

  React.useEffect(() => {
    let cancelled = false;
    const loadSunTimes = async () => {
      setSunTimes((prev) => ({ ...prev, loading: true }));
      try {
        const res = await fetch(
          `https://api.sunrise-sunset.org/json?lat=${SUN_LOCATION.lat}&lng=${SUN_LOCATION.lng}&formatted=0`
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (data.status !== 'OK') throw new Error(data.status || 'sunrise-sunset error');

        const sunrise = formatInTimeZone(new Date(data.results.sunrise), SUN_LOCATION.tz, 'h:mm a');
        const sunset = formatInTimeZone(new Date(data.results.sunset), SUN_LOCATION.tz, 'h:mm a');

        if (!cancelled) {
          setSunTimes({ sunrise, sunset, loading: false });
        }
      } catch (error) {
        console.warn('Sun times fetch failed:', error);
        if (!cancelled) {
          setSunTimes({ sunrise: null, sunset: null, loading: false });
        }
      }
    };
    loadSunTimes();
    return () => {
      cancelled = true;
    };
  }, []);

  const now = new Date();
  const phTime = formatInTimeZone(now, 'Asia/Manila', 'h:mm a');
  const phDate = formatInTimeZone(now, 'Asia/Manila', 'EEEE, d MMM');
  
  const weatherCondition = weatherData
    ? computeWeatherCondition(
        weatherData.temperature,
        weatherData.humidity,
        weatherData.pressure ?? null,
        weatherData.light ?? null,
        weatherData.rain ?? null,
        weatherData.windSpeed ?? null
      )
    : 'unknown';

  const weatherLabel = getWeatherConditionLabel(weatherCondition);
  const weatherIcon = getWeatherIcon(weatherCondition);

  // Calculate "feels like" temperature (simplified heat index)
  const feelsLike = weatherData?.temperature
    ? weatherData.temperature + (weatherData.humidity ? weatherData.humidity / 20 - 2 : 0)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Left Card: Location & Time */}
      <div
        className="p-8 rounded-3xl"
        style={{
          backgroundColor: 'rgba(30, 30, 35, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="text-gray-400 text-sm mb-2">Location</div>
        <div className="text-3xl font-bold mb-6">Philippines</div>
        <div className="text-6xl font-bold mb-2">{phTime}</div>
        <div className="text-gray-400 text-lg">{phDate}</div>
      </div>

      {/* Right Card: Current Weather */}
      <div
        className="p-8 rounded-3xl"
        style={{
          backgroundColor: 'rgba(30, 30, 35, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-6xl font-bold mb-2">
              {weatherData?.temperature !== null && weatherData?.temperature !== undefined
                ? `${weatherData.temperature.toFixed(1)}°C`
                : '--'}
            </div>
            <div className="text-gray-400 text-lg">
              Feels like: {feelsLike !== null && feelsLike !== undefined
                ? `${feelsLike.toFixed(1)}°C`
                : '--'}
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 flex items-center justify-center mb-2">
              <img
                src={weatherIcon}
                alt={weatherLabel}
                className="w-full h-full object-contain"
                style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
              />
            </div>
            <div className="text-xl font-semibold">{weatherLabel}</div>
          </div>
        </div>

        {/* Sunrise/Sunset (fetched from sunrise-sunset.org for Manila) */}
        <div className="flex gap-6 text-sm text-gray-400 mb-6">
          <div>
            ↑ Sunrise {sunTimes.sunrise ?? (sunTimes.loading ? 'Loading…' : '--')}
          </div>
          <div>
            ↓ Sunset {sunTimes.sunset ?? (sunTimes.loading ? 'Loading…' : '--')}
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-500/20">
              <Droplets className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Humidity</div>
              <div className="text-lg font-semibold">
                {weatherData?.humidity !== null && weatherData?.humidity !== undefined 
                  ? `${weatherData.humidity.toFixed(0)}%` 
                  : '--'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-500/20">
              <Wind className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Wind Speed</div>
              <div className="text-lg font-semibold">
                {weatherData?.windSpeed !== null && weatherData?.windSpeed !== undefined
                  ? `${(weatherData.windSpeed * 3.6).toFixed(1)}km/h`
                  : '--'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-500/20">
              <Gauge className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">Pressure</div>
              <div className="text-lg font-semibold">
                {weatherData?.pressure !== null && weatherData?.pressure !== undefined
                  ? `${weatherData.pressure.toFixed(0)}hPa`
                  : '--'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-yellow-500/20">
              <Sun className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <div className="text-xs text-gray-400">UV Index</div>
              <div className="text-lg font-semibold">
                {weatherData?.light !== null && weatherData?.light !== undefined
                  ? Math.round((weatherData.light / 100) * 11)
                  : '--'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

